import React, { useState } from 'react';
import api, { handleAPIError } from '../../services/api';

interface AuditPage {
  url: string;
  status: number;
  title: string;
  metaDescription: string;
  h1: string;
  wordCount: number;
  isThinContent: boolean;
  isRedirect: boolean;
  [key: string]: unknown;
}

interface AuditSummary {
  totalUrls: number;
  crawled: number;
  failed: number;
  totalIssues: number;
  issueCount: Record<string, number>;
}

interface AuditResponse {
  summary: AuditSummary;
  categories: Record<string, unknown[]>;
  pages: AuditPage[];
}

const ISSUE_SEVERITY: Record<string, { bg: string; text: string }> = {
  title: { bg: 'bg-red-100', text: 'text-red-800' },
  metaDescription: { bg: 'bg-orange-100', text: 'text-orange-800' },
  h1: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  status4xx: { bg: 'bg-red-100', text: 'text-red-800' },
  status5xx: { bg: 'bg-red-100', text: 'text-red-800' },
  redirectChain: { bg: 'bg-orange-100', text: 'text-orange-800' },
  thinContent: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
};

function getStatusColor(status: number): string {
  if (status === 0) return 'text-gray-500 bg-gray-100';
  if (status >= 200 && status < 300) return 'text-green-700 bg-green-100';
  if (status >= 300 && status < 400) return 'text-yellow-700 bg-yellow-100';
  return 'text-red-700 bg-red-100';
}

function getPageIssues(page: AuditPage): string[] {
  const issues: string[] = [];
  if (!page.title) issues.push('Missing title');
  if (!page.metaDescription) issues.push('Missing meta description');
  if (!page.h1) issues.push('Missing H1');
  if (page.isThinContent) issues.push('Thin content');
  if (page.isRedirect) issues.push('Redirect');
  if (page.status >= 400) issues.push(`Status ${page.status}`);
  return issues;
}

function truncateText(text: string, maxLen = 50): string {
  if (!text) return '-';
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 3) + '...';
}

const SiteAudit: React.FC = () => {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [limit, setLimit] = useState(500);
  const [thinContentThreshold, setThinContentThreshold] = useState(300);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AuditResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUrl = websiteUrl.trim();
    const trimmedSitemap = sitemapUrl.trim();
    if (!trimmedUrl || !trimmedSitemap) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await api.post<AuditResponse>('/seotools/site-audit', {
        websiteUrl: trimmedUrl,
        sitemapUrl: trimmedSitemap,
        limit,
        thinContentThreshold,
      });
      setData(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Site Audit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Crawl a website and identify SEO issues including missing titles, meta descriptions, H1 tags, thin content, and status errors.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
        <strong>Note:</strong> Large site audits may take several minutes depending on the number of URLs. The crawl limit determines the maximum number of pages to process.
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="audit-website" className="block text-sm font-medium text-gray-700 mb-1">
              Website URL <span className="text-red-500">*</span>
            </label>
            <input
              id="audit-website"
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
              required
            />
          </div>
          <div>
            <label htmlFor="audit-sitemap" className="block text-sm font-medium text-gray-700 mb-1">
              Sitemap URL <span className="text-red-500">*</span>
            </label>
            <input
              id="audit-sitemap"
              type="text"
              value={sitemapUrl}
              onChange={(e) => setSitemapUrl(e.target.value)}
              placeholder="https://example.com/sitemap.xml"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="audit-limit" className="block text-sm font-medium text-gray-700 mb-1">
              Crawl Limit
            </label>
            <input
              id="audit-limit"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 500)}
              min={1}
              max={10000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="audit-thin" className="block text-sm font-medium text-gray-700 mb-1">
              Thin Content Threshold (words)
            </label>
            <input
              id="audit-thin"
              type="number"
              value={thinContentThreshold}
              onChange={(e) => setThinContentThreshold(Number(e.target.value) || 300)}
              min={50}
              max={5000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={loading}
            />
          </div>
        </div>
        <div>
          <button
            type="submit"
            disabled={loading || !websiteUrl.trim() || !sitemapUrl.trim()}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Crawling...
              </span>
            ) : (
              'Start Audit'
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total URLs</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{data.summary.totalUrls}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Crawled</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{data.summary.crawled}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Failed</p>
              <p className="text-2xl font-bold text-red-700 mt-1">{data.summary.failed}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Issues</p>
              <p className="text-2xl font-bold text-orange-700 mt-1">{data.summary.totalIssues}</p>
            </div>
          </div>

          {/* Issue Breakdown */}
          {Object.keys(data.summary.issueCount).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Issue Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.summary.issueCount)
                  .sort(([, a], [, b]) => b - a)
                  .map(([issue, count]) => {
                    const severity = ISSUE_SEVERITY[issue] || { bg: 'bg-gray-100', text: 'text-gray-700' };
                    return (
                      <span
                        key={issue}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${severity.bg} ${severity.text}`}
                      >
                        <span className="font-bold">{count}</span>
                        {issue.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Pages Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">All Pages ({data.pages.length})</h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[600px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">H1</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Words</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issues</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.pages.map((page, idx) => {
                    const issues = getPageIssues(page);
                    return (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2 text-sm text-gray-900 font-mono max-w-xs truncate" title={page.url}>
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {truncateText(page.url, 60)}
                          </a>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${getStatusColor(page.status)}`}>
                            {page.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700" title={page.title}>
                          {truncateText(page.title)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700" title={page.h1}>
                          {truncateText(page.h1)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                          <span className={page.isThinContent ? 'text-red-600 font-semibold' : ''}>
                            {page.wordCount}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {issues.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {issues.map((issue) => (
                                <span
                                  key={issue}
                                  className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700"
                                >
                                  {issue}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-green-600 font-medium">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteAudit;
